import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';

import {
  CrudTable,
  DataGridColumnDefinition,
  StylizedNumber,
} from 'components';
import { TranslationNamespace } from 'i18n';
import { PayinProviderConversion, PayinProviderConversionTotals } from 'types';

type Props = {
  queryResult: UseQueryResult<PayinProviderConversion>;
};

const EMPTY_VALUE = '—';

export const ConversionFunnelTable: React.FC<Props> = ({ queryResult }) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.statistics.provider_conversion.funnel',
  });

  // A totals row with no orders behind it reads as data; show the empty state instead
  const rowsQueryResult = useMemo(() => {
    const totals = queryResult.data?.totals;
    return {
      ...queryResult,
      data: totals?.consideredCount ? [totals] : [],
    } as UseQueryResult<PayinProviderConversionTotals[]>;
  }, [queryResult]);

  const columns = useMemo(
    (): DataGridColumnDefinition<PayinProviderConversionTotals>[] => [
      {
        header: t('considered_count'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.consideredCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('sent_count'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.sentCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('not_sent_count'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.notSentCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('requisites_count'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.requisitesCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('cascade_lost_count'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.cascadeLostCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('completed_count'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.completedCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('not_completed_count'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.notCompletedCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('in_progress_count'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.inProgressCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('sent_conversion'),
        valueGetter: (item) => (
          <StylizedNumber value={item.sentConversion} unit="%" />
        ),
      },
      {
        header: t('requisites_conversion'),
        valueGetter: (item) => (
          <StylizedNumber value={item.requisitesConversion} unit="%" />
        ),
      },
      {
        header: t('completed_conversion'),
        // See ProviderConversionTable: no served orders means no conversion, not a zero one
        valueGetter: (item) =>
          item.requisitesCount > item.cascadeLostCount ? (
            <StylizedNumber value={item.completedConversion} unit="%" />
          ) : (
            EMPTY_VALUE
          ),
      },
      {
        header: t('end_to_end_conversion'),
        valueGetter: (item) => (
          <StylizedNumber value={item.endToEndConversion} unit="%" />
        ),
      },
    ],
    [t, tCommon],
  );

  return <CrudTable queryResult={rowsQueryResult} columns={columns} />;
};
